import { Plus, Loader2 } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/utils/cn'
import type { Proposta } from '@/types'
import { proposalStatusConfig } from './types'

interface SavedProposalsStripProps {
  proposals: Proposta[]
  loading: boolean
  editingId: string | null
  canEdit: boolean
  allowNew?: boolean
  onSelect: (proposta: Proposta) => void
  onNew: () => void
}

export function SavedProposalsStrip({
  proposals,
  loading,
  editingId,
  canEdit,
  allowNew = true,
  onSelect,
  onNew,
}: SavedProposalsStripProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1 text-xs text-text-muted">
        <Loader2 size={14} className="animate-spin" />
        Carregando rascunhos…
      </div>
    )
  }

  if (proposals.length === 0) return null

  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex flex-1 gap-1.5 overflow-x-auto pb-0.5 scrollbar-thin min-w-0">
        {proposals.map((p) => {
          const statusCfg = proposalStatusConfig[p.status] ?? proposalStatusConfig.rascunho
          const isActive = editingId === p.id
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p)}
              className={cn(
                'shrink-0 flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors',
                isActive
                  ? 'border-accent/50 bg-accent/10'
                  : 'border-white/[0.08] bg-white/[0.03] hover:border-white/[0.14] hover:bg-white/[0.05]',
              )}
            >
              <span className="text-xs font-semibold text-text-primary tabular-nums">
                {formatCurrency(p.totalFinal)}
              </span>
              <Badge variant={statusCfg.variant} className="text-[9px] px-1 py-0">
                {statusCfg.label}
              </Badge>
            </button>
          )
        })}
      </div>
      {canEdit && allowNew && (
        <button
          type="button"
          onClick={onNew}
          className="shrink-0 flex items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.04] px-2 py-1 text-[10px] font-medium text-text-muted hover:text-accent hover:border-accent/30 transition-colors"
        >
          <Plus size={11} />
          Nova
        </button>
      )}
    </div>
  )
}
